import { useState } from 'react';
import { useRouter } from 'next/router';
import classNames from 'classnames';

import { FilterToggle } from '@components/input/FilterToggle';
import { Svg } from '@components/svg/Svg';

import { programmeDateFilter } from '@common/utils/programmeDateFilter';
import { getSearchParams } from '@common/utils/programmeFilter';
import { getProgrammeGenreFilter } from '@common/utils/programmeGenreFilter';
import { programmeTypeFilter } from '@common/utils/programmeTypeFilter';
import { getProgrammeVenueFilter } from '@common/utils/programmeVenueFilter';
import { ProgrammePage } from '@interfaces';

import styles from './programme-filter-form.module.scss';

interface ProgrammeFilterProps {
	filterState: Map<string, string>;
	programme: ProgrammePage;
	setFilterState: React.Dispatch<React.SetStateAction<Map<string, string>>>;
	setViewState: React.Dispatch<React.SetStateAction<'grid' | 'list'>>;
	viewState: 'grid' | 'list';
}
export const ProgrammeFilterForm = ({
	filterState,
	programme,
	setFilterState,
	setViewState,
	viewState,
}: ProgrammeFilterProps) => {
	const router = useRouter();
	const [isExpanded, setIsExpanded] = useState(false);

	const filters = [
		programmeDateFilter,
		getProgrammeVenueFilter(programme),
		getProgrammeGenreFilter(programme),
	].filter((filter) => typeof filter !== 'undefined');

	const handleToggle = () => {
		setIsExpanded(!isExpanded);
	};

	const handleFormChange = async ({
		currentTarget,
	}: React.ChangeEvent<HTMLFormElement>) => {
		// Handle change to the form to update the url search params and initiate fetching data.
		const formData = new FormData(currentTarget);

		// Create searchParams from the formData
		const searchParams = getSearchParams({
			data: formData,
			programme,
		});

		// Create the url with updated search params
		const url = new URL(window.location.toString());
		url.search = searchParams.toString();
		// Clear the hash to prevent jumping
		url.hash = '';

		// Update the url
		router.push(url, undefined, { scroll: false, shallow: true });
	};

	const handleInputChange = ({
		currentTarget: { name, value },
	}: React.ChangeEvent<HTMLInputElement>) => {
		// Handle change to individual inputs to update state.
		const nextFilterState = new Map(filterState);

		if (value) {
			nextFilterState.set(name, value);
		} else {
			nextFilterState.delete(name);
		}

		setFilterState(nextFilterState);
	};

	const renderExpandButtonLabel = () => {
		if (!filterState.size) {
			return 'Filter by';
		}

		const shortLabel = `${filterState.size} selected`;
		const longLabel = `${filterState.size} filter${
			filterState.size === 1 ? '' : 's'
		} selected`;

		return (
			<>
				<span>{shortLabel}</span>
				<span>{longLabel}</span>
			</>
		);
	};

	return (
		<form
			className={classNames(
				styles['programme-filter-form'],
				'wave-background-bottom',
				{
					[styles['programme-filter-form--is-expanded']]: isExpanded,
				},
			)}
			id="filter"
			method="get"
			onChange={handleFormChange}
		>
			<div className={styles['programme-filter-form__toggles']}>
				<button
					className={styles['programme-filter-form__expand-button']}
					onClick={handleToggle}
					type="button"
				>
					{renderExpandButtonLabel()}
					<Svg
						className={styles['programme-filter-form__expand-icon']}
						id="chevron-down"
					/>
				</button>

				<div className={styles['programme-filter-form__view-filter']}>
					<FilterToggle
						checked={viewState === 'list'}
						id="filter-view-option-list"
						isRounded
						name="view"
						onChange={() => setViewState('list')}
						value="list"
					>
						List
					</FilterToggle>
					<FilterToggle
						checked={viewState === 'grid'}
						id="filter-view-option-grid"
						isRounded
						name="view"
						onChange={() => setViewState('grid')}
						value="grid"
					>
						Grid
					</FilterToggle>
				</div>
			</div>

			<div className={styles['programme-filter-form__filters']}>
				<div className={styles['programme-filter-form__type-filter']}>
					<p>{programmeTypeFilter.label}</p>
					{programmeTypeFilter.options.map(
						({ id, label, filterValue }, index) => {
							const stateValue = filterState.get(
								programmeTypeFilter.filterParameter,
							);
							const isChecked = stateValue
								? stateValue === filterValue
								: !index;

							return (
								<FilterToggle
									checked={isChecked}
									id={id}
									isRounded
									key={id}
									name={programmeTypeFilter.filterParameter}
									onChange={handleInputChange}
									value={filterValue}
								>
									{label}
								</FilterToggle>
							);
						},
					)}
				</div>

				<div className={styles['programme-filter-form__filter-groups']}>
					{filters.map((filter) => {
						return (
							<div
								className={styles['programme-filter-form__filter-group']}
								key={filter.id}
								style={
									{
										'--column-count': Math.ceil(filter.options.length / 5),
									} as React.CSSProperties
								}
							>
								<p>{filter.label}</p>
								<ul
									className={classNames(
										styles['programme-filter-form__list'],
										'list-unstyled',
									)}
								>
									{filter.options.map(({ id, label, filterValue }, index) => {
										const stateValue = filterState.get(filter.filterParameter);
										const isChecked = stateValue
											? stateValue === filterValue
											: !index;

										return (
											<li
												className={styles['programme-filter-form__filter']}
												key={id}
											>
												<FilterToggle
													checked={isChecked}
													id={id}
													name={filter.filterParameter}
													onChange={handleInputChange}
													value={!index ? '' : filterValue}
												>
													{label}
												</FilterToggle>
											</li>
										);
									})}
								</ul>
							</div>
						);
					})}
				</div>
			</div>
		</form>
	);
};
